import { Box, Button, Container, Link, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { roomPath, roomTypeLabel, type RoomType } from "@/deal-room/room-path";

interface FetchSupplier {
    supplierName?: string;
    roomType?: RoomType;
}

// Sign-in landing for supplier-facing rooms. Magic-link only — no password.
// Staff (Microsoft/AAD) sign-in is intentionally a quiet footer text link.
//
// When `?room=<slug>` is present we fetch the room (anonymous endpoint will
// 401 only on gated routes; a /api/rooms/<slug>/public-meta could return just
// the supplier name without auth — for now we just hide the supplier name if
// the fetch fails).
export function SignIn() {
    const [params] = useSearchParams();
    const slug = (params.get("room") ?? "").trim().toLowerCase();
    // Pick a post-auth destination. Order:
    //   1. explicit ?next= (any in-app path)
    //   2. /deal-room/<slug> when ?room= is set (a recipient was sent here)
    //   3. / when neither is set (staff hit /sign-in or root) — Home then
    //      does the three-way branch on /api/auth/me. Avoids landing on
    //      "/deal-room/" (empty slug → NotFound).
    const rawNext = params.get("next");
    const next =
        rawNext && rawNext.startsWith("/")
            ? rawNext
            : slug
              ? roomPath(slug)
              : "/";

    const supplierQuery = useQuery({
        enabled: Boolean(slug),
        queryKey: ["sign-in-supplier", slug],
        queryFn: async (): Promise<FetchSupplier> => {
            const res = await fetch(`/api/rooms/${slug}/public-meta`);
            if (!res.ok) return {};
            const raw = (await res.json()) as { supplierName?: string; roomType?: RoomType };
            return { supplierName: raw.supplierName, roomType: raw.roomType };
        },
        retry: false,
    });

    return (
        <Container
            maxWidth="sm"
            sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", py: { xs: 6, md: 10 } }}
        >
            <Stack spacing={5} alignItems="center" textAlign="center" sx={{ flexGrow: 1 }}>
                <Box
                    component="img"
                    src="/brand/bison-logo-color.png"
                    alt="Bison Commerce"
                    sx={{ width: 200, display: "block" }}
                />
                <Box>
                    <Typography variant="h1" sx={site.h1}>
                        {supplierQuery.data?.supplierName ? (
                            <>Sign in to {supplierQuery.data.supplierName}&rsquo;s {roomTypeLabel(supplierQuery.data.roomType)}</>
                        ) : (
                            `Sign in to your ${roomTypeLabel(supplierQuery.data?.roomType)}`
                        )}
                    </Typography>
                    <Typography sx={{ ...site.lead, mt: 2.5, maxWidth: 480, mx: "auto" }}>
                        Enter your email address to receive a login link.
                    </Typography>
                </Box>
                <Box sx={{ width: "100%", maxWidth: 460 }}>
                    <SignInForm slug={slug} next={next} />
                </Box>
            </Stack>
            <StaffSignInFooter next={next} />
        </Container>
    );
}

// Styling mirrors the BisonCommerce.com site (bisoncommerce-website:
// app/globals.css + components/ContactForm.tsx) so the public sign-in reads as
// the same product: Inter, 800-weight tight headings, the white bordered form
// card, label-above fields with a subtle gray focus border, the orange
// `btn-primary`. Values are the site's tokens, not the deal-room MUI theme's.
const site = {
    navy: "#001f37",
    orange: "#f03a11",
    body: "#4b5367",
    muted: "#6b7387",
    h1: {
        fontSize: "2.25rem",
        fontWeight: 800,
        letterSpacing: "-0.02em",
        lineHeight: 1.05,
        color: "#001f37",
    },
    lead: { fontSize: "1.125rem", lineHeight: 1.625, color: "#6b7387" },
    card: {
        p: { xs: 3, sm: 4 },
        bgcolor: "#fff",
        border: "1px solid #dadde5",
        borderRadius: "10px",
        boxShadow: "0 1px 2px rgba(0, 31, 56, 0.05)",
        width: "100%",
        textAlign: "left" as const,
    },
    label: { fontSize: "0.875rem", fontWeight: 500, color: "#001f37" },
    input: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            bgcolor: "#fff",
            fontSize: "0.875rem",
            color: "#001f37",
            "& fieldset": { borderColor: "#dadde5" },
            "&:hover fieldset": { borderColor: "#dadde5" },
            "&.Mui-focused fieldset": { borderColor: "#b3b9c6", borderWidth: 1 },
        },
        "& .MuiOutlinedInput-input": { px: 1.5, py: 1.25 },
    },
    btnPrimary: {
        bgcolor: "#f03a11",
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.9375rem",
        lineHeight: 1,
        px: 3,
        py: 1.75,
        borderRadius: "8px",
        boxShadow: "0 4px 6px rgba(0, 31, 56, 0.06), 0 2px 4px rgba(0, 31, 56, 0.04)",
        transition: "transform 0.15s ease, background-color 0.15s ease",
        "&:hover": {
            bgcolor: "#ff4a1f",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 6px rgba(0, 31, 56, 0.06), 0 2px 4px rgba(0, 31, 56, 0.04)",
        },
        "&.Mui-disabled": { bgcolor: "#f03a11", color: "#fff", opacity: 0.6, boxShadow: "none" },
    },
    caption: { fontSize: "0.875rem", color: "#6b7387" },
};

// Deliberately quiet: a single "Bison Commerce Login" text link in the page
// footer, no button, no divider. Clicking it starts the Microsoft (AAD) sign-in.
function StaffSignInFooter({ next }: { next: string }) {
    const href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(
        `/api/auth/aad-bridge?next=${encodeURIComponent(next)}`,
    )}`;
    return (
        <Box component="footer" sx={{ pt: 6, textAlign: "center" }}>
            <Link
                href={href}
                underline="hover"
                sx={{ ...site.caption, fontWeight: 400, "&:hover": { color: site.navy } }}
            >
                Bison Commerce Login
            </Link>
        </Box>
    );
}

interface FormProps {
    slug: string;
    next: string;
}

function SignInForm({ slug, next }: FormProps) {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState<"idle" | "submitting" | "sent" | "error">("idle");
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!email) return;
        setStatus("submitting");
        setErrorMsg(null);

        try {
            const res = await fetch("/api/auth/request-link", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, slug, next }),
            });
            if (!res.ok) {
                const data = (await res.json().catch(() => ({}))) as { error?: string };
                throw new Error(data.error ?? `request failed (${res.status})`);
            }
            setStatus("sent");
        } catch (err) {
            setStatus("error");
            setErrorMsg(err instanceof Error ? err.message : "Something went wrong.");
        }
    }

    if (status === "sent") {
        // Mirrors the site's contact-form success block: centred heading with
        // the Nucleo paper-plane icon beneath it, then left-aligned plain text.
        const bodyText = { fontSize: "1rem", lineHeight: 1.6, color: site.body };
        return (
            <Box sx={site.card}>
                <Typography
                    component="h2"
                    sx={{
                        fontSize: "1.25rem",
                        fontWeight: 800,
                        letterSpacing: "-0.02em",
                        color: site.navy,
                        textAlign: "center",
                    }}
                >
                    Check your inbox
                </Typography>
                <Box
                    component="img"
                    src="/brand/icons/paperPlane.svg"
                    alt=""
                    aria-hidden
                    sx={{ mt: 2, width: 48, height: 48, display: "block", mx: "auto" }}
                />
                <Typography sx={{ ...bodyText, mt: 3 }}>
                    If {email} is on the authorized access list, you&apos;ll receive a sign-in
                    link from us soon.
                </Typography>
                <Typography sx={{ ...bodyText, mt: 3.9, textAlign: "center" }}>
                    Wrong email?{" "}
                    <Box
                        component="button"
                        type="button"
                        onClick={() => {
                            setStatus("idle");
                            setErrorMsg(null);
                        }}
                        sx={{
                            background: "none",
                            border: 0,
                            color: site.navy,
                            cursor: "pointer",
                            font: "inherit",
                            p: 0,
                            textDecoration: "underline",
                            textDecorationColor: site.orange,
                            textDecorationThickness: "2px",
                            textUnderlineOffset: "2px",
                        }}
                    >
                        Try a different one.
                    </Box>
                </Typography>
            </Box>
        );
    }

    return (
        <Box component="form" onSubmit={onSubmit} sx={site.card}>
            <Stack spacing={0.75}>
                <Typography component="label" htmlFor="sign-in-email" sx={site.label}>
                    Email address
                </Typography>
                <TextField
                    id="sign-in-email"
                    type="email"
                    autoFocus
                    required
                    fullWidth
                    size="small"
                    autoComplete="email"
                    // Same tags as the site's contact form: keep 1Password and
                    // LastPass from offering to fill or save this field.
                    slotProps={{ htmlInput: { "data-1p-ignore": true, "data-lpignore": "true" } }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={status === "submitting"}
                    sx={site.input}
                />
            </Stack>
            {errorMsg && (
                <Typography sx={{ mt: 2, fontSize: "0.875rem", fontWeight: 500, color: site.orange }}>
                    {errorMsg}
                </Typography>
            )}
            <Button
                type="submit"
                fullWidth
                disableElevation
                disabled={status === "submitting"}
                sx={{ ...site.btnPrimary, mt: 2.5 }}
            >
                {status === "submitting" ? "Sending..." : "Email me a link"}
            </Button>
        </Box>
    );
}
